import { useState, type ReactNode } from 'react';
import { AlertTriangle, BookOpen, FlaskConical, ListChecks, ChevronDown, ChevronRight } from 'lucide-react';

// ============================================================================
// Summary Data Types
// ============================================================================

export interface ProcedureStep {
  number: number;
  title: string;
  details: string[];
  safetyNote?: string;
  manualRef?: string;
}

export interface SummaryData {
  experimentId: string;
  title: string;
  objectives: string[];
  safetyNotes: string[];
  equipment: string[];
  chemicals: string[];
  procedure: ProcedureStep[];
  keyFormulas?: string[];
  tips?: string[];
}

// ============================================================================
// ExperimentSummary Component
// ============================================================================

interface ExperimentSummaryProps {
  data: SummaryData;
  flowchart: ReactNode;
}

export default function ExperimentSummary({ data, flowchart }: ExperimentSummaryProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    objectives: true,
    safety: true,
    equipment: false,
    procedure: true,
    flowchart: true,
    formulas: false,
    tips: false,
  });

  const toggle = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="exp-summary">
      <div className="exp-summary-header">
        <BookOpen size={20} />
        <h2>{data.title}</h2>
        <span className="exp-summary-badge">Reference Guide</span>
      </div>

      {/* Learning Objectives */}
      <Section
        id="objectives"
        title="Learning Objectives"
        icon={<ListChecks size={16} />}
        expanded={expandedSections.objectives}
        onToggle={() => toggle('objectives')}
      >
        <ul className="exp-summary-list">
          {data.objectives.map((obj, i) => (
            <li key={i}>{obj}</li>
          ))}
        </ul>
      </Section>

      {/* Safety Notes */}
      <Section
        id="safety"
        title="Safety Notes"
        icon={<AlertTriangle size={16} />}
        expanded={expandedSections.safety}
        onToggle={() => toggle('safety')}
        variant="warning"
      >
        <ul className="exp-summary-list safety">
          {data.safetyNotes.map((note, i) => (
            <li key={i}>{note}</li>
          ))}
        </ul>
      </Section>

      {/* Equipment & Chemicals */}
      <Section
        id="equipment"
        title="Equipment & Chemicals"
        icon={<FlaskConical size={16} />}
        expanded={expandedSections.equipment}
        onToggle={() => toggle('equipment')}
      >
        <div className="exp-summary-cols">
          <div>
            <h4>Equipment</h4>
            <ul className="exp-summary-list compact">
              {data.equipment.map((eq, i) => (
                <li key={i}>{eq}</li>
              ))}
            </ul>
          </div>
          {data.chemicals.length > 0 && (
            <div>
              <h4>Chemicals</h4>
              <ul className="exp-summary-list compact">
                {data.chemicals.map((ch, i) => (
                  <li key={i}>{ch}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Section>

      {/* Procedure Steps */}
      <Section
        id="procedure"
        title="Procedure"
        icon={<ListChecks size={16} />}
        expanded={expandedSections.procedure}
        onToggle={() => toggle('procedure')}
      >
        <div className="exp-summary-procedure">
          {data.procedure.map((step) => (
            <div key={step.number} className="procedure-step">
              <div className="procedure-step-header">
                <span className="step-num">{step.number}</span>
                <span className="step-title">{step.title}</span>
                {step.manualRef && (
                  <span className="step-ref">{step.manualRef}</span>
                )}
              </div>
              <ul className="procedure-step-details">
                {step.details.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
              {step.safetyNote && (
                <div className="procedure-safety-note">
                  <AlertTriangle size={12} />
                  <span>{step.safetyNote}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      {/* Flowchart */}
      <Section
        id="flowchart"
        title="Procedure Flowchart"
        icon={<BookOpen size={16} />}
        expanded={expandedSections.flowchart}
        onToggle={() => toggle('flowchart')}
      >
        {flowchart}
      </Section>

      {/* Key Formulas */}
      {data.keyFormulas && data.keyFormulas.length > 0 && (
        <Section
          id="formulas"
          title="Key Formulas"
          icon={<BookOpen size={16} />}
          expanded={expandedSections.formulas}
          onToggle={() => toggle('formulas')}
        >
          <div className="exp-summary-formulas">
            {data.keyFormulas.map((f, i) => (
              <div key={i} className="formula-item">{f}</div>
            ))}
          </div>
        </Section>
      )}

      {/* Tips */}
      {data.tips && data.tips.length > 0 && (
        <Section
          id="tips"
          title="Tips & Best Practices"
          icon={<BookOpen size={16} />}
          expanded={expandedSections.tips}
          onToggle={() => toggle('tips')}
        >
          <ul className="exp-summary-list">
            {data.tips.map((tip, i) => (
              <li key={i}>{tip}</li>
            ))}
          </ul>
        </Section>
      )}

      <style>{summaryStyles}</style>
    </div>
  );
}

// ============================================================================
// Collapsible Section
// ============================================================================

interface SectionProps {
  id: string;
  title: string;
  icon: ReactNode;
  expanded: boolean;
  onToggle: () => void;
  variant?: 'default' | 'warning';
  children: ReactNode;
}

function Section({ title, icon, expanded, onToggle, variant = 'default', children }: SectionProps) {
  return (
    <div className={`exp-summary-section ${variant} ${expanded ? 'expanded' : ''}`}>
      <button className="section-header" onClick={onToggle}>
        {icon}
        <span>{title}</span>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {expanded && <div className="section-content">{children}</div>}
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const summaryStyles = `
  .exp-summary {
    padding: 16px 24px;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
    overflow-y: auto;
  }

  .exp-summary-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(148, 163, 184, 0.2);
    color: #e2e8f0;
  }

  .exp-summary-header h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }

  .exp-summary-badge {
    margin-left: auto;
    padding: 3px 10px;
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.4);
    border-radius: 12px;
    font-size: 11px;
    color: #a5b4fc;
  }

  .exp-summary-section {
    background: rgba(30, 41, 59, 0.5);
    border: 1px solid rgba(148, 163, 184, 0.15);
    border-radius: 8px;
    overflow: hidden;
  }

  .exp-summary-section.warning {
    border-color: rgba(245, 158, 11, 0.3);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 10px 14px;
    background: transparent;
    border: none;
    color: #e2e8f0;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;
  }

  .section-header:hover {
    background: rgba(148, 163, 184, 0.08);
  }

  .section-header svg:last-child {
    margin-left: auto;
    opacity: 0.5;
  }

  .warning .section-header {
    color: #fbbf24;
  }

  .section-content {
    padding: 4px 14px 14px;
  }

  .exp-summary-list {
    margin: 0;
    padding-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .exp-summary-list li {
    font-size: 12.5px;
    color: #cbd5e1;
    line-height: 1.5;
  }

  .exp-summary-list.safety li {
    color: #fde68a;
  }

  .exp-summary-list.compact {
    gap: 2px;
  }

  .exp-summary-list.compact li {
    font-size: 12px;
  }

  .exp-summary-cols {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  .exp-summary-cols h4 {
    margin: 0 0 6px;
    font-size: 12px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .exp-summary-procedure {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .procedure-step {
    background: rgba(15, 23, 42, 0.5);
    border: 1px solid rgba(148, 163, 184, 0.1);
    border-radius: 6px;
    padding: 10px 12px;
  }

  .procedure-step-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
  }

  .step-num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #0d9488;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .step-title {
    font-size: 13px;
    font-weight: 600;
    color: #e2e8f0;
  }

  .step-ref {
    margin-left: auto;
    font-size: 10px;
    color: #64748b;
    font-style: italic;
  }

  .procedure-step-details {
    margin: 0;
    padding-left: 36px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .procedure-step-details li {
    font-size: 12px;
    color: #94a3b8;
    line-height: 1.5;
  }

  .procedure-safety-note {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 6px;
    padding: 4px 8px;
    background: rgba(245, 158, 11, 0.1);
    border-radius: 4px;
    font-size: 11px;
    color: #fbbf24;
  }

  .exp-summary-formulas {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .formula-item {
    padding: 6px 12px;
    background: rgba(15, 23, 42, 0.6);
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 4px;
    font-family: 'Courier New', monospace;
    font-size: 12.5px;
    color: #a5b4fc;
  }
`;
